import React from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import PropTypes from "prop-types";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

import { settings, animations } from "../../../settings";
import {
  FormField,
  Password,
  SignInWithGoogle,
  SignInWithMicrosoft,
} from "../../../components";
import { TwoColumnLayout } from "../../../templates";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";

export const redirectToClient = (url) => {
  window.location = url;
};

export const leftContent = (
  <div className="absolute inset-0">
    <picture>
      <source
        media="(min-width: 1024px)"
        srcSet="/design/images/login-page/sign-in-desktop.webp?v=3"
      />
      <img
        src="/design/images/login-page/sign-in-tablet.webp"
        alt="Sign in illustration"
        className="w-full h-full object-cover"
      />
    </picture>
    <div className="absolute z-10" style={{ top: "2.5rem", left: "2.5rem" }}>
      <img
        src="/design/images/login-page/breathe_logo_white.svg"
        alt="Breathe logo icon"
        className="h-8 w-9"
      />
    </div>
  </div>
);

export const LoginForm = ({
  username,
  setUsername,
  password,
  setPassword,
  loading,
  usernameIsValidEmail,
  usernameIsNotValidEmail,
  signIn,
}) => {
  const { animate } = settings;
  const { form } = animations;

  const { isEnabled: isGoogleOAuthEnabled, isLoading: isGoogleOAuthLoading } =
    useFeatureFlag("socialSignin");
  const { isEnabled: isMicrosoftOAuthEnabled, isLoading: isMicrosoftOAuthLoading } =
    useFeatureFlag("socialSigninForMicrosoft");

  const isOAuthEnabled = isGoogleOAuthEnabled || isMicrosoftOAuthEnabled;
  const isOAuthLoading = isGoogleOAuthLoading || isMicrosoftOAuthLoading;

  return (
    <>
      <Helmet>
        <title>Sign in with Breathe</title>
      </Helmet>

      <TwoColumnLayout leftContent={leftContent}>
        <div className="flex flex-col items-center md:items-start w-full mb-0">
          <h1
            className="text-[30px] font-bold leading-[36px] login-text-foreground mb-0 font-rubik text-center md:text-left"
            style={{ fontWeight: 700, fontSize: "30px", lineHeight: "36px" }}
          >
            Sign in with Breathe
          </h1>
          <p className="login-text-muted mt-0 mb-6 font-rubik login-subtitle text-center md:text-left">
            Login to your account
          </p>
        </div>

        <motion.form
          variants={form}
          initial={animate ? "hidden" : ""}
          animate={animate ? "show" : ""}
          className="flex flex-col gap-6 items-center justify-center w-full"
        >
          <div className="flex flex-col gap-3 items-start w-full">
            <FormField
              id="email-input"
              type="email"
              label="Email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="me@example.com"
              autoComplete="off"
            />

            <div className="flex flex-col gap-2 items-start w-full">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="password-input"
                className="text-sm font-normal leading-none login-text-foreground font-inter"
              >
                Password
              </label>
              <Password
                password={password}
                setPassword={setPassword}
                variant="static"
                showLabel={false}
                id="password-input"
                label="Password"
              />
            </div>

            <button
              type="submit"
              className={`w-full login-button rounded-md login-bg-primary text-white text-sm font-normal flex items-center justify-center font-inter ${
                usernameIsValidEmail() ? "cursor-pointer" : "opacity-50 cursor-not-allowed"
              }`}
              onClick={(e) => {
                e.preventDefault();
                signIn();
              }}
              disabled={usernameIsNotValidEmail()}
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin aria-hidden />
              ) : (
                "Login"
              )}
            </button>
          </div>

          {!isOAuthLoading && isOAuthEnabled && (
            <div className="relative w-full py-2.5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t login-border-input" />
              </div>
              <div className="relative flex justify-center">
                <span className="px-2 bg-white text-sm login-text-muted font-rubik">
                  Or continue with
                </span>
              </div>
            </div>
          )}

          {!isOAuthLoading && isOAuthEnabled && (
            <div className="flex flex-col gap-4 items-start w-full social-login-buttons">
              {isGoogleOAuthEnabled && <SignInWithGoogle />}
              {isMicrosoftOAuthEnabled && <SignInWithMicrosoft />}
            </div>
          )}
        </motion.form>
      </TwoColumnLayout>
    </>
  );
};

LoginForm.propTypes = {
  username: PropTypes.string.isRequired,
  setUsername: PropTypes.func.isRequired,
  password: PropTypes.string.isRequired,
  setPassword: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  usernameIsValidEmail: PropTypes.func.isRequired,
  usernameIsNotValidEmail: PropTypes.func.isRequired,
  signIn: PropTypes.func.isRequired,
};
